import React, { useEffect, useRef, useState } from "react";

/**
 * Instant Gaming Affiliate Banner.
 *
 * Loads the official IG partner-loader exactly once per page-life, no matter
 * how often this component remounts (StrictMode, route changes, etc.).
 * Renders a skeleton while the script is fetching, then fades the banner in.
 *
 * Affiliate tag: gamer-7b3bbe4 (FicoPilot)
 */
const IG_SCRIPT_SRC =
  "https://www.instant-gaming.com/api/banner/partner/loader.js";
const IG_AFFILIATE_ID = "gamer-7b3bbe4";
const BANNER_CLASS = "ficopilot-ig-banner";

// Module-level singleton flag — survives unmount/remount cycles.
let _igScriptInjected = false;

export default function InstantGamingBanner({ lang = "de" }) {
  const containerRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Make the IG config available before the script reads it.
    // We merge so multiple banners on the same page can coexist later.
    const existing = window.igBannerConfig || {};
    window.igBannerConfig = {
      ...existing,
      lang,
      igr: IG_AFFILIATE_ID,
      banners: Array.from(
        new Set([...(existing.banners || []), BANNER_CLASS])
      ),
    };

    // Helper to observe DOM mutations inside the container so we can
    // flip the loaded flag (drives the fade-in + hides skeleton).
    const observer = new MutationObserver(() => {
      if (containerRef.current && containerRef.current.children.length > 0) {
        setLoaded(true);
        observer.disconnect();
      }
    });
    if (containerRef.current) {
      observer.observe(containerRef.current, { childList: true, subtree: true });
    }

    // Inject script exactly once.
    if (!_igScriptInjected) {
      _igScriptInjected = true;
      const s = document.createElement("script");
      s.src = IG_SCRIPT_SRC;
      s.defer = true;
      s.async = true;
      s.dataset.partner = "instant-gaming";
      document.head.appendChild(s);
    }

    // Safety net: if mutation never fires (already-cached script + no DOM
    // change), reveal the slot after 2.5s so the skeleton can't get stuck.
    const t = setTimeout(() => setLoaded(true), 2500);

    return () => {
      observer.disconnect();
      clearTimeout(t);
    };
  }, [lang]);

  return (
    <div className="relative">
      {/* Skeleton — shown until IG renders into the container */}
      {!loaded && (
        <div
          aria-hidden="true"
          data-testid="ig-banner-skeleton"
          className="absolute inset-0 rounded-xl overflow-hidden"
        >
          <div className="h-full w-full bg-gradient-to-r from-zinc-900/40 via-zinc-800/40 to-zinc-900/40 animate-pulse rounded-xl" />
        </div>
      )}

      {/* The slot Instant Gaming injects into */}
      <div
        ref={containerRef}
        className={`${BANNER_CLASS} ig-fade min-h-[140px] sm:min-h-[160px] transition-opacity duration-700 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
        data-testid="ig-banner-slot"
      />
    </div>
  );
}
